import { useState, useEffect, useCallback } from "react";
import {
  Heart, Coins, Repeat, Banknote, ShoppingBag, Search, ArrowDownLeft, ArrowUpRight,
  MoreVertical, Archive, ArchiveRestore, Trash2, Receipt, TrendingUp, TrendingDown, Wallet,
} from "lucide-react";
import api, { fileUrl, apiErr } from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Heart as HeartIcon } from "lucide-react";
import ReceiptModal from "./ReceiptModal";
import ThankYouModal from "./ThankYouModal";
import { toast } from "sonner";

const KIND = {
  tip: { icon: Heart, label: "Tip" },
  pledge: { icon: Repeat, label: "Monthly pledge" },
  jar_contribution: { icon: Coins, label: "Jar contribution" },
  jar_split: { icon: Coins, label: "Jar share" },
  cashout: { icon: Banknote, label: "Cash-out" },
  shop: { icon: ShoppingBag, label: "Shop purchase" },
};
const KINDS = ["", "tip", "pledge", "jar_contribution", "jar_split", "cashout", "shop"];
const RANGES = [["all", "All time"], ["30d", "30 days"], ["7d", "7 days"], ["year", "This year"]];

function Card({ label, value, sub, icon: Icon, tone = "bronze", testid }) {
  const tones = { sage: "text-sage bg-sage/10", copper: "text-copper bg-copper/10", bronze: "text-bronze bg-penny/15" };
  return (
    <div data-testid={testid} className="bg-card rounded-2xl border border-border p-4">
      <div className={`w-9 h-9 rounded-full flex items-center justify-center mb-2 ${tones[tone]}`}><Icon className="w-4.5 h-4.5" /></div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="font-serif text-2xl font-bold text-bronze tabular-nums">{value}</p>
      {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}

function Regular({ p }) {
  return (
    <div className="flex flex-col items-center w-20 shrink-0 text-center">
      <img src={fileUrl(p.avatar_url) || `https://api.dicebear.com/7.x/lorelei/svg?seed=${p.user_id}`} alt="" className="w-12 h-12 rounded-full bg-secondary object-cover" />
      <p className="text-xs font-medium truncate w-full mt-1">{p.name}</p>
      <p className="text-[10px] text-muted-foreground">{p.count}× · ${p.total.toFixed(0)}</p>
      {p.recurring && <span className="text-[9px] text-copper font-semibold">monthly</span>}
    </div>
  );
}

export default function TransactionCenter() {
  const [data, setData] = useState(null);
  const [view, setView] = useState("active");
  const [direction, setDirection] = useState("all");
  const [kind, setKind] = useState("");
  const [range, setRange] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [thankTarget, setThankTarget] = useState(null);

  const load = useCallback(async () => {
    const params = new URLSearchParams({ view, direction, kind, range, q });
    try { const { data } = await api.get(`/transactions?${params}`); setData(data); }
    catch (e) { toast.error(apiErr(e)); }
  }, [view, direction, kind, range, q]);
  useEffect(() => { const t = setTimeout(load, q ? 300 : 0); return () => clearTimeout(t); }, [load, q]);

  const setState = async (ref, state) => {
    try {
      await api.post("/transactions/state", { ref, state });
      toast.success(state === "archived" ? "Archived" : state === "deleted" ? "Removed from your history" : "Restored");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!data) return <div className="py-16 text-center text-copper font-serif">Loading your transactions…</div>;
  const s = data.summary;

  return (
    <div data-testid="transaction-center" className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card testid="summary-received" label="Total received" value={`$${s.received_total.toFixed(2)}`} sub={`$${s.received_this_month.toFixed(2)} this month`} icon={ArrowDownLeft} tone="sage" />
        <Card testid="summary-given" label="Total donated" value={`$${s.given_total.toFixed(2)}`} sub={`$${s.given_this_month.toFixed(2)} this month`} icon={ArrowUpRight} tone="copper" />
        <Card testid="summary-net" label="Net" value={`${s.net < 0 ? "−" : ""}$${Math.abs(s.net).toFixed(2)}`} sub={s.net >= 0 ? "received − donated" : "donated more than received"} icon={s.net >= 0 ? TrendingUp : TrendingDown} tone="bronze" />
        <Card testid="summary-cashed" label="Cashed out" value={`$${s.cashed_out_total.toFixed(2)}`} sub={`$${s.shop_spent_total.toFixed(2)} spent in shop`} icon={Wallet} tone="bronze" />
      </div>

      {/* Regulars */}
      <div className="grid md:grid-cols-2 gap-3">
        <div className="bg-card rounded-2xl border border-border p-4" data-testid="regular-donors">
          <p className="font-serif font-semibold text-bronze mb-3">Your regular donors 💛</p>
          {data.regulars.donors.length === 0 ? <p className="text-sm text-muted-foreground">No repeat donors yet.</p> : (
            <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1">{data.regulars.donors.map((p) => <Regular key={p.user_id} p={p} />)}</div>
          )}
        </div>
        <div className="bg-card rounded-2xl border border-border p-4" data-testid="regular-supporting">
          <p className="font-serif font-semibold text-bronze mb-3">People you support 🤝</p>
          {data.regulars.supporting.length === 0 ? <p className="text-sm text-muted-foreground">You haven't supported anyone repeatedly yet.</p> : (
            <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1">{data.regulars.supporting.map((p) => <Regular key={p.user_id} p={p} />)}</div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-card/60 border border-border rounded-2xl p-3 space-y-3">
        <div className="flex flex-wrap gap-2 items-center">
          {[["active", "Active"], ["archived", "Archived"]].map(([v, l]) => (
            <button key={v} data-testid={`txn-view-${v}`} onClick={() => setView(v)}
              className={`text-xs font-medium px-3 py-1.5 rounded-full ${view === v ? "bg-bronze text-white" : "bg-secondary text-bronze"}`}>{l}</button>
          ))}
          <span className="w-px h-5 bg-border mx-1" />
          {[["all", "All"], ["in", "Received"], ["out", "Given"]].map(([v, l]) => (
            <button key={v} data-testid={`txn-dir-${v}`} onClick={() => setDirection(v)}
              className={`text-xs font-medium px-3 py-1.5 rounded-full ${direction === v ? "bg-copper text-white" : "bg-secondary text-bronze"}`}>{l}</button>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="txn-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, note, type…" className="pl-9 rounded-full h-9" />
          </div>
          <select data-testid="txn-kind" value={kind} onChange={(e) => setKind(e.target.value)} className="rounded-full h-9 px-3 text-sm border border-border bg-background">
            {KINDS.map((k) => <option key={k || "all"} value={k}>{k ? (KIND[k]?.label || k) : "All types"}</option>)}
          </select>
          <select data-testid="txn-range" value={range} onChange={(e) => setRange(e.target.value)} className="rounded-full h-9 px-3 text-sm border border-border bg-background">
            {RANGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>

      {/* List */}
      <div className="space-y-2" data-testid="txn-list">
        {data.transactions.length === 0 ? (
          <p className="text-center text-muted-foreground py-12">No transactions{view === "archived" ? " archived" : ""} yet.</p>
        ) : data.transactions.map((t) => {
          const meta = KIND[t.kind] || { icon: Coins, label: t.kind };
          const Ic = meta.icon;
          const inbound = t.direction === "in";
          const title = t.counterparty ? t.counterparty.name : (t.anonymous ? "Anonymous friend" : (t.item ? `${t.item.emoji} ${t.item.name}` : meta.label));
          return (
            <div key={t.ref} data-testid={`txn-${t.ref}`} className="flex items-center gap-3 bg-card rounded-2xl border border-border p-3.5 hover:shadow-sm transition-shadow">
              <button onClick={() => setSelected(t)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${inbound ? "bg-sage/15 text-sage" : "bg-copper/10 text-copper"}`}><Ic className="w-5 h-5" /></div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate flex items-center gap-1.5">
                    {title}
                    {t.recurring && <span className="text-[9px] font-semibold text-copper bg-copper/10 px-1.5 py-0.5 rounded-full">MONTHLY</span>}
                    {t.archived && <span className="text-[9px] text-muted-foreground bg-secondary px-1.5 py-0.5 rounded-full">ARCHIVED</span>}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{meta.label} · {new Date(t.created_at).toLocaleDateString()} {t.message ? `· ${t.message}` : ""}</p>
                </div>
                <p className={`font-mono font-bold tabular-nums shrink-0 ${inbound ? "text-sage" : "text-bronze"}`}>{inbound ? "+" : "−"}${t.amount.toFixed(2)}</p>
              </button>
              {inbound && t.kind === "tip" && t.counterparty && (
                <button data-testid={`txn-thank-btn-${t.ref}`} onClick={() => setThankTarget(t.counterparty)} title="Say thanks"
                  className="p-1.5 rounded-full text-copper hover:bg-copper/10 shrink-0"><HeartIcon className="w-4 h-4" /></button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid={`txn-menu-${t.ref}`} className="p-1.5 rounded-full hover:bg-secondary text-muted-foreground shrink-0"><MoreVertical className="w-4 h-4" /></button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {inbound && t.kind === "tip" && t.counterparty && (
                    <DropdownMenuItem data-testid={`txn-thank-${t.ref}`} onClick={() => setThankTarget(t.counterparty)} className="text-copper">
                      <HeartIcon className="w-4 h-4 mr-2" /> Say thanks
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => setSelected(t)}><Receipt className="w-4 h-4 mr-2" /> View receipt</DropdownMenuItem>
                  {t.archived ? (
                    <DropdownMenuItem data-testid={`txn-restore-${t.ref}`} onClick={() => setState(t.ref, "active")}><ArchiveRestore className="w-4 h-4 mr-2" /> Restore</DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem data-testid={`txn-archive-${t.ref}`} onClick={() => setState(t.ref, "archived")}><Archive className="w-4 h-4 mr-2" /> Archive</DropdownMenuItem>
                  )}
                  <DropdownMenuItem data-testid={`txn-delete-${t.ref}`} onClick={() => setState(t.ref, "deleted")} className="text-destructive"><Trash2 className="w-4 h-4 mr-2" /> Delete from history</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        })}
      </div>

      <ReceiptModal txn={selected} onClose={() => setSelected(null)} />
      <ThankYouModal recipient={thankTarget} onClose={() => setThankTarget(null)} />
    </div>
  );
}
